/**
 * The in-game rail shape (SRVR-0356): the points the game and the desktop editor draw a rail through.
 *
 * Both draw a rail with `LineSmoother.SmoothLine(points, 0.8)` (CurvedLine package, called from
 * `Game_LineWaveCustom.RenderLine`), over the head and then every node, in file units (x, y as in the
 * file, z = time_ms × 0.02). Each axis is a Unity `AnimationCurve` keyed at the point's INDEX, with every
 * key's tangents set by `SmoothTangents(i, 0)`: the mean of the slopes either side, one-sided at the ends.
 * Each span is then sampled `trunc(distance / 0.8) − 1` times between its points, the distance being the
 * straight 3-D one — so a span shorter than 1.6 units is drawn straight, which is the in-game look the
 * web's own Catmull-Rom tube smooths away.
 */
import * as THREE from 'three';
import type { Note } from '../../types/beatmap';
import { GRID_SCALE } from '../../types/beatmap';
import { NOTE_Y_OFFSET } from './placementGeometry';

export type Point3 = [number, number, number];

/** `Game_LineWaveCustom.RenderLine`'s segment size. */
export const GAME_RAIL_SEGMENT_SIZE = 0.8;

/** `AnimationCurve.SmoothTangents(i, 0)` for keys one apart: the mean of the two neighbouring slopes. */
export function smoothTangents(values: readonly number[]): number[] {
  const n = values.length;
  if (n < 2) return values.map(() => 0);
  return values.map((_, i) => {
    if (i === 0) return values[1] - values[0];
    if (i === n - 1) return values[n - 1] - values[n - 2];
    return 0.5 * (values[i] - values[i - 1]) + 0.5 * (values[i + 1] - values[i]);
  });
}

/** `AnimationCurve.Evaluate` between two keys one apart: the cubic Hermite with their tangents. */
export function hermite(p0: number, m0: number, p1: number, m1: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return (2 * t3 - 3 * t2 + 1) * p0 + (t3 - 2 * t2 + t) * m0 + (-2 * t3 + 3 * t2) * p1 + (t3 - t2) * m1;
}

/** `LineSmoother.SmoothLine`: every input point, with each span's samples between it and the next. */
export function smoothLine(points: readonly Point3[], segmentSize = GAME_RAIL_SEGMENT_SIZE): Point3[] {
  const n = points.length;
  const tangents = [0, 1, 2].map(a => smoothTangents(points.map(p => p[a])));
  const out: Point3[] = [];
  for (let i = 0; i < n; i++) {
    out.push([...points[i]] as Point3);
    if (i + 1 >= n) continue;
    const a = points[i];
    const b = points[i + 1];
    const segments = Math.trunc(Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]) / segmentSize);
    for (let s = 1; s < segments; s++) {
      const t = s / segments;
      out.push([0, 1, 2].map(ax => hermite(a[ax], tangents[ax][i], b[ax], tangents[ax][i + 1], t)) as Point3);
    }
  }
  return out;
}

/** A rail's points as the game draws them: the head, then its nodes, in file units. */
export function gameRailPoints(note: Pick<Note, 'position' | 'rail_nodes'>): Point3[] {
  return smoothLine([[...note.position] as Point3, ...(note.rail_nodes ?? []).map(p => [...p] as Point3)]);
}

/**
 * The rail's tube in the scene in its in-game shape: the game's points placed as the smooth tube places
 * its nodes (x, y × the grid scale, z by beat × NJS), joined by straight pieces, as the game's
 * LineRenderer joins them. Same return shape as `RailLine`'s smooth tube, so the two are interchangeable.
 */
export function gameRailTube(
  note: Pick<Note, 'position' | 'rail_nodes' | 'time_beats'>, bpm: number, njs: number, radius: number,
): { geometry: THREE.TubeGeometry; durBeats: number } | null {
  if (!note.rail_nodes || !note.rail_nodes.length) return null;
  const beatOfZ = (z: number) => (z / 0.02 / 1000) * (bpm / 60);
  const pts = gameRailPoints(note).map(([x, y, z]) => new THREE.Vector3(
    x * GRID_SCALE * 10, y * GRID_SCALE * 10 + NOTE_Y_OFFSET, -beatOfZ(z) * njs,
  ));
  // The head sits where the smooth tube puts it, on its beat.
  pts[0].z = -note.time_beats * njs;
  const path = new THREE.CurvePath<THREE.Vector3>();
  for (let i = 1; i < pts.length; i++) {
    if (pts[i].distanceToSquared(pts[i - 1]) > 1e-12) path.add(new THREE.LineCurve3(pts[i - 1], pts[i]));
  }
  if (!path.curves.length) return null;
  const endBeats = beatOfZ(note.rail_nodes[note.rail_nodes.length - 1][2]);
  const geometry = new THREE.TubeGeometry(path, Math.max(pts.length * 8, 32), radius, 8, false);
  return { geometry, durBeats: Math.max(0.05, endBeats - note.time_beats) };
}
